import { useEffect, useRef } from 'react'
import type { VideoProject } from './video-projects'
import './project-video.css'

export function ProjectVideo({ project, preview = false, width = 1080, height = 1350 }: {
  project: Pick<VideoProject, 'title' | 'video' | 'previewVideo' | 'poster'>; preview?: boolean; width?: number; height?: number
}) {
  const ref = useRef<HTMLVideoElement>(null)
  const source = preview ? project.previewVideo ?? project.video : project.video

  useEffect(() => {
    const video = ref.current!
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    // Set the DOM properties before assigning a source, including Safari's default mute state.
    video.muted = true
    video.defaultMuted = true
    let visible = false
    const load = () => {
      if (video.getAttribute('src') !== source) {
        video.preload = 'auto'
        video.src = source
      }
    }
    const sync = () => {
      video.autoplay = visible && !document.hidden && !reducedMotion?.matches
      if (video.autoplay) {
        load()
        void video.play().catch(() => { /* Keep the poster visible when autoplay is blocked. */ })
      } else video.pause()
    }
    const retryAutoplay = () => { if (video.autoplay) sync() }
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.15
      if (visible && !preview) load() // Keep native playback controls usable with reduced motion.
      sync()
    }, { threshold: 0.15 })
    const preloader = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !reducedMotion?.matches) load()
    }, { rootMargin: '200px 0px' })
    sync()
    observer?.observe(video)
    preloader?.observe(video)
    if (!observer) { visible = true; if (!preview) load(); sync() }
    reducedMotion?.addEventListener('change', sync)
    video.addEventListener('canplay', retryAutoplay)
    document.addEventListener('visibilitychange', sync)
    window.addEventListener('pageshow', sync)
    return () => {
      observer?.disconnect()
      preloader?.disconnect()
      reducedMotion?.removeEventListener('change', sync)
      video.removeEventListener('canplay', retryAutoplay)
      document.removeEventListener('visibilitychange', sync)
      window.removeEventListener('pageshow', sync)
      video.autoplay = false
      video.pause()
    }
  }, [source, preview])

  return (
    <div className={`project-video${preview ? ' project-video-preview' : ''}`} style={{ aspectRatio: `${width} / ${height}` }}>
      <video ref={ref} poster={project.poster} width={width} height={height} autoPlay muted loop playsInline preload="none"
        controls={!preview} aria-label={`${project.title} demo`} />
    </div>
  )
}
